// Exercise-search pure helpers and filter option tables, lifted verbatim out of
// `ManualWorkoutEditor` in Phase 7A.
//
// Behaviour is unchanged and stays frozen by blueprint D23 / section 4.1:
// identical `rankExercises` field-rank scoring, identical option tables and
// muscle->focus cascade source, identical search limits, identical
// excluded-match detection. Phase 7B migrates only the search PRESENTATION.

export function normalizeMatchValue(value) {
  return String(value || "").trim().toLowerCase();
}

export function hasExcludedExerciseMatch(poolResponse, query) {
  const normalizedQuery = normalizeMatchValue(query);

  if (!normalizedQuery) {
    return false;
  }

  return (poolResponse?.excluded || []).some((exercise) =>
    [exercise?.exerciseId, exercise?.name]
      .map(normalizeMatchValue)
      .filter(Boolean)
      .some((value) => value.includes(normalizedQuery))
  );
}

function getFieldMatchRank(values, query) {
  const normalizedValues = Array.isArray(values)
    ? values.map(normalizeMatchValue).filter(Boolean)
    : [normalizeMatchValue(values)].filter(Boolean);

  let containsMatch = null;

  for (const value of normalizedValues) {
    if (!value.includes(query)) {
      continue;
    }

    if (value.startsWith(query)) {
      return 0;
    }

    if (containsMatch == null) {
      containsMatch = 1;
    }
  }

  return containsMatch;
}

export function rankExercises(exercises, query) {
  const normalizedQuery = normalizeMatchValue(query);

  if (!normalizedQuery) {
    return exercises;
  }

  return exercises
    .map((exercise, originalIndex) => {
      const nameRank = getFieldMatchRank(exercise.name, normalizedQuery);
      const aliasesRank = getFieldMatchRank(exercise.aliases, normalizedQuery);
      const keywordsRank = getFieldMatchRank(exercise.keywords, normalizedQuery);

      let tier = 3;
      let startsWithRank = 1;

      if (nameRank != null) {
        tier = 0;
        startsWithRank = nameRank;
      } else if (aliasesRank != null) {
        tier = 1;
        startsWithRank = aliasesRank;
      } else if (keywordsRank != null) {
        tier = 2;
        startsWithRank = keywordsRank;
      }

      return {
        exercise,
        originalIndex,
        tier,
        startsWithRank,
        normalizedName: normalizeMatchValue(exercise.name),
      };
    })
    .sort((left, right) => {
      if (left.tier !== right.tier) {
        return left.tier - right.tier;
      }

      if (left.startsWithRank !== right.startsWithRank) {
        return left.startsWithRank - right.startsWithRank;
      }

      if (left.normalizedName !== right.normalizedName) {
        return left.normalizedName.localeCompare(right.normalizedName);
      }

      return left.originalIndex - right.originalIndex;
    })
    .map((entry) => entry.exercise);
}


export const DEFAULT_SEARCH_LIMIT = 50;
export const FILTER_ONLY_SEARCH_LIMIT = 150;

export const MUSCLE_OPTIONS = [
  { value: "chest", label: "chest" },
  { value: "back", label: "back" },
  { value: "shoulders", label: "shoulders" },
  { value: "biceps", label: "biceps" },
  { value: "triceps", label: "triceps" },
  { value: "quadriceps", label: "quadriceps" },
  { value: "hamstrings", label: "hamstrings" },
  { value: "glutes", label: "glutes" },
  { value: "calves", label: "calves" },
  { value: "abs", label: "abs" },
];

export const FOCUS_OPTIONS_BY_MUSCLE = {
  chest: [
    { value: "upper_chest", label: "upper chest" },
    { value: "mid_chest", label: "mid chest" },
    { value: "lower_chest", label: "lower chest" },
  ],
  back: [
    { value: "lats", label: "lats" },
    { value: "upper_back", label: "upper back" },
    { value: "mid_back", label: "mid back" },
    { value: "lower_back", label: "lower back" },
  ],
  shoulders: [
    { value: "front_delts", label: "front delts" },
    { value: "side_delts", label: "side delts" },
    { value: "rear_delts", label: "rear delts" },
  ],
  biceps: [
    { value: "biceps_long_head", label: "biceps long head" },
    { value: "biceps_short_head", label: "biceps short head" },
    { value: "brachialis", label: "brachialis" },
  ],
  triceps: [
    { value: "triceps_long_head", label: "triceps long head" },
    { value: "triceps_lateral_head", label: "triceps lateral head" },
    { value: "forearms", label: "forearms" },
  ],
  quadriceps: [],
  hamstrings: [],
  glutes: [
    { value: "glute_max", label: "glutes max" },
    { value: "glute_med", label: "glutes med" },
  ],
  calves: [],
  abs: [
    { value: "upper_abs", label: "upper abs" },
    { value: "lower_abs", label: "lower abs" },
    { value: "obliques", label: "obliques" },
  ],
};

export const EQUIPMENT_OPTIONS = [
  { value: "barbell", label: "barbell" },
  { value: "dumbbell", label: "dumbbell" },
  { value: "cable", label: "cable" },
  { value: "smith machine", label: "smith machine" },
  { value: "bodyweight", label: "bodyweight" },
  { value: "band", label: "band" },
  { value: "kettlebell", label: "kettlebell" },
  { value: "machine", label: "machine" },
  { value: "assisted", label: "assisted" },
];

export const TYPE_OPTIONS = [
  { value: "strength", label: "strength" },
  { value: "warmup", label: "warmup" },
  { value: "mobility", label: "mobility" },
  { value: "cardio", label: "cardio" },
  { value: "plyometric", label: "plyometric" },
];

export const DIFFICULTY_OPTIONS = [
  { value: "beginner", label: "beginner" },
  { value: "intermediate", label: "intermediate" },
  { value: "advanced", label: "advanced" },
];

export function getFilterPillLabel(label, selectedValues, options) {
  if (!selectedValues.length) {
    return label;
  }

  if (selectedValues.length === 1) {
    return (
      options.find((option) => option.value === selectedValues[0])?.label ||
      selectedValues[0]
    );
  }

  return `${label} (${selectedValues.length})`;
}

export function getExerciseTrainingType(exercise) {
  return String(exercise?.trainingType || "").trim().toLowerCase();
}
